class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
    this.handleUpsellAddClick = this.handleUpsellAddClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener("click", this.handleUpsellAddClick);
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleUpsellAddClick);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleUpsellAddClick(e) {
    const button = e.target.closest("[data-cart-upsell-add]");
    if (!button) return;

    e.preventDefault();
    if (button.getAttribute("aria-disabled") === "true") return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute("aria-disabled", "true");
    button.classList.add("loading");

    const sectionsToRender = this.getSectionsToRender();
    const sectionsUrl = window.location.pathname;
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);
    formData.append(
      "sections",
      sectionsToRender.map((section) => section.id),
    );
    formData.append("sections_url", sectionsUrl);
    config.body = formData;

    const shouldOpenDrawer = !this.isOpen;
    const discountCode = this.dataset.crossSellDiscountCode?.trim();
    const shouldApplyCrossSellDiscount = Boolean(
      button.closest("[data-apply-cross-sell-discount]"),
    );
    const needsDiscount = Boolean(
      discountCode && shouldApplyCrossSellDiscount,
    );

    const finishUpsellAdd = (renderState, { openDrawer = false, publishUpdate = true } = {}) => {
      this.renderContents(renderState, openDrawer);

      if (publishUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-upsell",
          productVariantId: variantId,
          cartData: renderState,
        });
      }
    };

    if (shouldOpenDrawer) {
      this.toggleDrawerClasses();
    }

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) return;

        finishUpsellAdd(response, { openDrawer: false });

        if (!needsDiscount || this.hasCrossSellDiscount(response, discountCode)) {
          return;
        }

        const hasBundle = await this.cartHasBundle(response);
        if (!hasBundle) return;

        this.applyCrossSellDiscount(
          discountCode,
          sectionsToRender,
          sectionsUrl,
        )
          .then((cartState) => {
            if (!cartState || cartState.status) return;

            if (cartState.sections) {
              finishUpsellAdd({ sections: cartState.sections });
              return;
            }

            return this.fetchCartDrawerSections().then((sections) => {
              finishUpsellAdd({ sections });
            });
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        button.removeAttribute("aria-disabled");
        button.classList.remove("loading");
      });
  }

  applyCrossSellDiscount(discountCode, sectionsToRender, sectionsUrl) {
    return fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: discountCode,
        sections: sectionsToRender.map((section) => section.id),
        sections_url: sectionsUrl,
      }),
    }).then((response) => response.json());
  }

  hasCrossSellDiscount(cartState, discountCode) {
    if (!cartState) return false;

    const normalizedCode = discountCode?.trim().toLowerCase();
    const cartDiscounts = cartState.cart_level_discount_applications;

    if (Array.isArray(cartDiscounts) && cartDiscounts.length > 0) {
      if (!normalizedCode) return true;

      return cartDiscounts.some((discount) => {
        const title = String(discount.title || "").trim().toLowerCase();
        return title === normalizedCode || title.includes(normalizedCode);
      });
    }

    const items = cartState.items || [];

    return items.some((item) => {
      const lineDiscounts = item.line_level_discount_allocations;
      return Array.isArray(lineDiscounts) && lineDiscounts.length > 0;
    });
  }

  async cartHasBundle(cartState) {
    const items = cartState?.items;
    if (!Array.isArray(items) || items.length === 0) return false;

    const checks = await Promise.all(
      items.map((item) => {
        if (!item.handle) return false;

        return fetch(`${window.Shopify.routes.root}products/${item.handle}.js`)
          .then((response) => response.json())
          .then((product) =>
            (product.tags || []).some(
              (tag) => String(tag).toLowerCase() === "discupsell",
            ),
          )
          .catch(() => false);
      }),
    );

    return checks.some(Boolean);
  }

  fetchCartDrawerSections() {
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(",");

    return fetch(
      `${window.Shopify.routes.root}?sections=${sectionIds}`,
    ).then((response) => response.json());
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      requestAnimationFrame(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const sectionNode = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);

    return sectionNode?.innerHTML ?? "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Drawer markup is replaced by cart-drawer section renders.
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
